import { Box, Link } from "@mui/material";
import CustomTable from "components/Table/CustomTable";
import { Link as LinkBase } from "react-router-dom";
import { Requirement } from "types/requirement";
import { FC, useMemo } from "react";
import { TABLE_ACTIONS } from "helpers/constants";
import { DomainColorSquares } from "components/Requirements";
import { requirementsHaveDomainSwatches } from "utils/requirementDomains";

type CompetencyRequirementsProps = {
  competencies: Requirement[];
  onDeleteRequirement?: (academicYearRequirementId: string) => void;
  onViewChart?: (requirement: Requirement) => void;
};

export const CompetencyRequirements: FC<CompetencyRequirementsProps> = ({
  competencies,
  onDeleteRequirement,
  onViewChart,
}) => {
  const handleActionClick = (action: string, id: string) => {
    if (action === TABLE_ACTIONS.DELETE && onDeleteRequirement) {
      const requirement = competencies.find(
        (r) => r.requirement_id === id,
      ) as any;
      if (requirement?.academic_year_requirement_id) {
        onDeleteRequirement(requirement.academic_year_requirement_id);
      }
    }
  };

  const showDomainsColumn = requirementsHaveDomainSwatches(competencies);

  const columns = useMemo(
    () => [
      {
        id: "name",
        label: "Req ID",
        value: "name",
      },
      {
        id: "description",
        label: "Requirement Description",
        value: (row: any) => {
          return row.description?.length && row.description?.length > 128
            ? row.description?.slice(0, 128)
            : row.description;
        },
      },
      ...(showDomainsColumn
        ? [
            {
              id: "domains",
              label: "Domains",
              value: (row: any) => <DomainColorSquares requirement={row} />,
            },
          ]
        : []),
      {
        id: "link",
        value: (row: Requirement) => (
          <Link
            component={LinkBase}
            to={`/requirements/competency/${row.requirement_id}`}
          >
            View More
          </Link>
        ),
      },
      ...(onViewChart
        ? [
            {
              id: "view_chart",
              label: "",
              value: (row: Requirement) => (
                <Link
                  component="button"
                  variant="body1"
                  underline="always"
                  color="primary"
                  onClick={() => onViewChart(row)}
                  sx={{
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    font: "inherit",
                    padding: 0,
                  }}
                >
                  View Chart
                </Link>
              ),
            },
          ]
        : []),
      ...(onDeleteRequirement ? [{ id: "actions", value: "" }] : []),
    ],
    [onDeleteRequirement, onViewChart, showDomainsColumn],
  );

  return (
    <Box>
      <CustomTable
        entities={competencies}
        columns={columns}
        handleActionClick={handleActionClick}
        fieldToFilter={(row: any, filterText: string) =>
          row.name.toLowerCase().includes(filterText.toLowerCase()) ||
          row.description?.toLowerCase().includes(filterText.toLowerCase())
        }
        isSelectable={false}
        allowedMenuItems={
          onDeleteRequirement ? [TABLE_ACTIONS.DELETE] : []
        }
        extraActionItems={[]}
        idColumn={"requirement_id"}
        enableCardViewInSmallDevices={true}
      />
    </Box>
  );
};
